import { useCallback, useEffect, useRef, useState } from 'react';

import api from '../../../../../services/api';
import { useAuth } from '../../../../../hooks/auth';

export interface Emissao {
  id: number;
  status: string;
  numero: number | null;
  serie: number | null;
  chave_acesso: string | null;
  motivo_rejeicao: string | null;
}

export interface Terminal {
  id: number;
  device_code: string;
  serie_nfce: number;
}

export interface SituacaoFiscal {
  emissao: Emissao | null;
  empresaHabilitada: boolean;
  terminais: Terminal[];
  terminalPadraoId: number | null;
  opcaoAutomatica: string | null;
  avisoTerminal: string | null;
  cpfConsumidor: string | null;
  nomeConsumidor: string | null;
  emissaoAutomatica: boolean;
}

export const STATUS_FINAIS_OK = ['authorized', 'contingency'];

/** Intervalo da consulta enquanto a nota está sendo gerada. */
const INTERVALO_MS = 8000;

/** A nota ainda vai mudar de situação sozinha (job ou SEFAZ)? */
export function nfceEmAndamento(situacao: SituacaoFiscal | null): boolean {
  if (!situacao?.empresaHabilitada) return false;
  const status = situacao.emissao?.status;
  if (status === 'processing') return true;
  return !situacao.emissao && situacao.emissaoAutomatica;
}

/**
 * Situação da NFC-e de um pedido, reconsultada a cada poucos segundos enquanto
 * a nota está sendo gerada (emissão automática ou em processamento na SEFAZ).
 */
export function useSituacaoNfce(
  pedidoId: number | undefined,
  habilitado: boolean,
): {
  situacao: SituacaoFiscal | null;
  carregando: boolean;
  recarregar: () => Promise<SituacaoFiscal | null>;
  setSituacao: React.Dispatch<React.SetStateAction<SituacaoFiscal | null>>;
} {
  const { user } = useAuth();
  const [situacao, setSituacao] = useState<SituacaoFiscal | null>(null);
  const [carregando, setCarregando] = useState(false);
  // Descarta respostas de um pedido anterior que cheguem depois da troca.
  const pedidoAtual = useRef(pedidoId);
  pedidoAtual.current = pedidoId;

  const recarregar = useCallback(async () => {
    if (!pedidoId || !habilitado) return null;
    setCarregando(true);
    try {
      const { data } = await api.get<SituacaoFiscal>(
        `/companies/${user.empresa_id}/pedidos/${pedidoId}/nfce`,
      );
      if (pedidoAtual.current !== pedidoId) return null;
      setSituacao(data);
      return data;
    } finally {
      if (pedidoAtual.current === pedidoId) setCarregando(false);
    }
  }, [habilitado, pedidoId, user.empresa_id]);

  useEffect(() => {
    setSituacao(null);
    recarregar().catch(() => undefined);
  }, [recarregar]);

  const emAndamento = nfceEmAndamento(situacao);
  useEffect(() => {
    if (!emAndamento) return undefined;
    const timer = setInterval(() => {
      recarregar().catch(() => undefined);
    }, INTERVALO_MS);
    return () => clearInterval(timer);
  }, [emAndamento, recarregar]);

  return { situacao, carregando, recarregar, setSituacao };
}
